// Library
import React from 'react';
// Spinner
import { PulseLoader } from 'react-spinners';

// CSS Style for this module
import styles from './button.module.scss';

/**
 * Interface for Button props
 */
export interface ButtonProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * preset type which has its own styling and functionality
	 */
	type?: 'primary' | 'secondary' | 'tertiary';
	/**
	 * preset size of the button
	 */
	size?: 'small' | 'large' | 'giant' | 'auto';
	/**
	 * Button contents
	 */
	children: React.ReactNode;
	/**
	 * Optional click handler
	 */
	onClick?: () => void;
	/**
	 * If true, disable all interactions for this component
	 */
	disabled?: boolean;
	/**
	 * If true, replace button text by a spinner and defuse interaction
	 */
	isLoading?: boolean;
	/**
	 * Set tab index for accessibility
	 */
	tabIndex?: number;
	/**
	 * Set button focused by default
	 */
	autoFocus?: boolean;
	/**
	 * aria-label
	 */
	ariaLabel?: string;
	/**
	 * Indicates the behavior type of the <button> element. Set as 'button' by default
	 * see : https://developer.mozilla.org/en-US/docs/Web/API/HTMLButtonElement/type
	 */
	purposeType?: 'button' | 'reset' | 'submit';
	/**
	 * testId
	 */
	testId?: string;
}

/**
 * An styled general button
 * @param {ButtonProps} props
 * @returns  a button
 */

export const Button = ({
	customStyles,
	size,
	type = 'tertiary',
	tabIndex,
	children,
	disabled,
	isLoading = false,
	onClick,
	purposeType = 'button',
	ariaLabel,
	autoFocus,
	testId,
	...props
}: ButtonProps) => {
	ariaLabel = ariaLabel || (typeof children === 'string' ? children : 'button');
	return (
		<button
			{...props}
			className={[
				styles[`button-${type}`],
				size && styles[`button-${size}`],
				isLoading && styles['button-loading'],
				customStyles,
			].join(' ')}
			disabled={disabled || isLoading}
			onClick={
				disabled || isLoading
					? () => {
							return;
						}
					: onClick
			}
			type={purposeType}
			aria-label={ariaLabel}
			tabIndex={tabIndex}
			autoFocus={autoFocus}
			data-testid={testId}
			{...props}
		>
			{!isLoading ? (
				children
			) : (
				<div style={{ position: 'relative' }}>
					<div style={{ visibility: 'hidden' }}>{children}</div>
					<PulseLoader className={[styles.spinner, styles[`spinner-${type}`]].join(' ')} />
				</div>
			)}
		</button>
	);
};
